/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import {
	Typography,
	Button,
	Input,
	Row,
	Col,
	Checkbox,
	Collapse,
} from "antd";
import { useDispatch, connect, useSelector } from "react-redux";
import * as actions from "./actions";
import { useHistory } from "react-router-dom";
import {
	getReviewAppointments,
	getCurrentPatient,
	getSaveBookingState,
} from "./reducers";
import { getCurrentLocation } from "../Location/reducers";
import Payment from "./Payment";
import {
	CheckCircleOutlined,
	CalendarOutlined,
	ClockCircleOutlined,
	UserOutlined,
	EditOutlined,
	DownOutlined,
	RightOutlined,
} from "@ant-design/icons";
import {
	changePatientNote,
	cancelReservationRequest,
} from "../Booking/actions";
import { getAvailabilityRequests } from "../Booking/reducers";
import ContentContainer from "../../components/ContentContainer";
import FullNameWithTitleLabel from "../../components/FullNameWithTitleLabel";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { trackEvent, trackFBStandardEvent, trackButtonClick } from "utils/analytics";
import usePageView from "../../hooks/usePageView";

const { Text } = Typography;
const { Panel } = Collapse;

function Confirm({
	currentPractice,
	reviewBookingRequest,
	reviewBookings,
	match,
	saveBookingRequest,
	saveBookingState,
	startBookingProcess,
	availabilityRequests,
	deleteRequestsUnlessValidSlot,
	openPaymentDrawer,
}) {
	const { params } = match;

	const history = useHistory();
	const dispatch = useDispatch();
	const patientNote = useSelector((state) => state.booking.patientNote);
	const [acceptTerms, setAcceptTerms] = React.useState(false);
	const [showNotes, setShowNotes] = React.useState(false);
	const [detailsExpanded, setDetailsExpanded] = React.useState(false);

	// Track page view with step info
	usePageView("Confirm Booking", 5, "confirmation", 95);

	React.useEffect(() => {
		reviewBookingRequest(params.locationId);
		deleteRequestsUnlessValidSlot();
	}, []);

	const serviceTotal = React.useMemo(() => {
		let sumTotal = 0;
		if (!reviewBookings) return sumTotal;
		reviewBookings.map(({ appointmentReason }) => {
			return (sumTotal += appointmentReason.onlineBookingsPrice);
		});
		return sumTotal;
	}, [reviewBookings]);

	const surcharge = React.useMemo(() => {
		let sumTotal = 0;
		if (!reviewBookings) return sumTotal;
		reviewBookings.map(({ appointmentReason }) => {
			return (sumTotal += appointmentReason.onlineBookingsPrice);
		});
		return (
			sumTotal * currentPractice.stripeZavyFee + currentPractice.stripeBaseFee
		);
	}, [reviewBookings]);

	const totalWithSurcharge = React.useMemo(() => {
		let sumTotal = 0;
		if (!reviewBookings) return sumTotal;
		reviewBookings.map(({ appointmentReason }) => {
			return (sumTotal += appointmentReason.onlineBookingsPrice);
		});
		if (currentPractice?.applyPatientSurcharge) {
			return (
				sumTotal +
				(sumTotal * currentPractice.stripeZavyFee +
					currentPractice.stripeBaseFee)
			);
		}
		return sumTotal;
	}, [reviewBookings]);

	const handleMaybePay = React.useCallback(() => {
		trackButtonClick('confirm_booking_button', {
			requires_payment: currentPractice.canReceiveOnlinePayments && serviceTotal > 0.0,
			total_amount: totalWithSurcharge,
		});

		if (currentPractice.canReceiveOnlinePayments && serviceTotal > 0.0) {
			trackEvent("payment_drawer_opened", { amount: totalWithSurcharge });
			openPaymentDrawer();
		} else {
			trackEvent("booking_confirmed", {
				total_amount: serviceTotal,
				has_payment: false,
				services_count: reviewBookings?.length || 0,
			});
			trackFBStandardEvent("Schedule");
			dispatch(startBookingProcess());
			saveBookingRequest(
				{ ...params, patientNote },
				currentPractice?.successUrl,
			);
		}
	}, [currentPractice, serviceTotal, patientNote, totalWithSurcharge, reviewBookings]);

	const confirmationButtonText = React.useMemo(
		() =>
			currentPractice.canReceiveOnlinePayments && serviceTotal > 0.0
				? "Pay and Confirm"
				: "Confirm Booking",
		[currentPractice, serviceTotal],
	);

	const onChange = (e) => {
		dispatch(changePatientNote(e.target.value));
	};

	const handleGoBack = React.useCallback(() => {
		dispatch(
			cancelReservationRequest(currentPractice.id, availabilityRequests),
		);
		history.go(-history.length + 1);
	}, []);

	// Get first booking for summary display
	const firstBooking = reviewBookings?.[0];
	const hasTerms = currentPractice?.termsAndConditions;

	const confirmButton = reviewBookings && reviewBookings.length > 0 ? (
		<Button
			disabled={hasTerms && !acceptTerms}
			size="large"
			icon={<CheckCircleOutlined />}
			block
			style={{
				backgroundColor: (hasTerms && !acceptTerms)
					? "#fafafa"
					: currentPractice?.themeColor,
				borderColor: (hasTerms && !acceptTerms)
					? "#eee"
					: currentPractice?.themeColor,
				height: 48,
				fontWeight: 500,
			}}
			loading={!!saveBookingState}
			type="primary"
			onClick={() => handleMaybePay()}
		>
			{confirmationButtonText}
		</Button>
	) : null;

	return (
		<ContentContainer
			pageTitle="Review"
			step="5"
			themeColor={currentPractice?.themeColor}
			stepPercentage="95"
			showNext={false}
			extra={confirmButton}
			backButton={
				<Button
					type="text"
					shape="circle"
					onClick={() => handleGoBack()}
					icon={<ArrowLeftOutlined />}
				/>
			}
		>
			<Payment
				practiceId={params.locationId}
				{...{ serviceTotal, surcharge, totalWithSurcharge }}
			/>

			{/* Booking Summary Card */}
			{firstBooking && (
				<div
					style={{
						background: "#fff",
						borderRadius: 12,
						border: "1px solid #f0f0f0",
						overflow: "hidden",
						marginBottom: 16,
						marginTop: 24,
					}}
				>
					{/* Header - always visible */}
					<div
						onClick={() => setDetailsExpanded(!detailsExpanded)}
						style={{
							padding: "16px 20px",
							cursor: "pointer",
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							borderBottom: detailsExpanded ? "1px solid #f0f0f0" : "none",
						}}
					>
						<div>
							<Text strong style={{ fontSize: 16, display: "block", marginBottom: 4 }}>
								{firstBooking.appointmentReason.name}
							</Text>
							<Text type="secondary" style={{ fontSize: 13 }}>
								{firstBooking.date} at {firstBooking.startTime}
							</Text>
						</div>
						<div style={{ color: "#999", fontSize: 12 }}>
							{detailsExpanded ? <DownOutlined /> : <RightOutlined />}
						</div>
					</div>

					{/* Expanded Details */}
					{detailsExpanded && (
						<div style={{ padding: "16px 20px" }}>
							{reviewBookings?.map(
								({ practitioner, appointmentReason, date, startTime }, index) => (
									<div
										key={`${startTime}-${appointmentReason.id}-${index}`}
										style={{
											marginBottom: index < reviewBookings.length - 1 ? 16 : 0,
											paddingBottom: index < reviewBookings.length - 1 ? 16 : 0,
											borderBottom: index < reviewBookings.length - 1 ? "1px solid #f5f5f5" : "none",
										}}
									>
										{/* Service name - only show for additional bookings */}
										{index > 0 && (
											<Text strong style={{ display: "block", marginBottom: 12 }}>
												{appointmentReason.name}
											</Text>
										)}

										{/* Details grid */}
										<div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
											<div style={{ display: "flex", alignItems: "center", gap: 10 }}>
												<CalendarOutlined style={{ color: "#999", fontSize: 14 }} />
												<Text style={{ fontSize: 14 }}>{date}</Text>
											</div>

											<div style={{ display: "flex", alignItems: "center", gap: 10 }}>
												<ClockCircleOutlined style={{ color: "#999", fontSize: 14 }} />
												<Text style={{ fontSize: 14 }}>
													{startTime}
													{appointmentReason.minutes > 0 && ` for ${appointmentReason.minutes} mins`}
												</Text>
											</div>

											<div style={{ display: "flex", alignItems: "center", gap: 10 }}>
												<UserOutlined style={{ color: "#999", fontSize: 14 }} />
												<Text style={{ fontSize: 14 }}>
													<FullNameWithTitleLabel name={practitioner.fullNameWithTitle} />
												</Text>
											</div>

											{/* Price */}
											{(["deposit"].includes(appointmentReason?.prepaymentType) &&
												appointmentReason?.onlineBookingsPrice > 0) ? (
												<div style={{ display: "flex", alignItems: "center", gap: 10 }}>
													<span style={{ color: "#999", fontSize: 14, width: 14 }}>$</span>
													<Text style={{ fontSize: 14 }}>
														{appointmentReason.onlineBookingsPrice?.toFixed(2)}
														<Text type="secondary" style={{ marginLeft: 4 }}>(deposit)</Text>
													</Text>
												</div>
											) : (
												appointmentReason?.showTotalCost && appointmentReason.price > 0 && (
													<div style={{ display: "flex", alignItems: "center", gap: 10 }}>
														<span style={{ color: "#999", fontSize: 14, width: 14 }}>$</span>
														<Text style={{ fontSize: 14 }}>
															{appointmentReason.price?.toFixed(2)}
														</Text>
													</div>
												)
											)}
										</div>
									</div>
								),
							)}
						</div>
					)}
				</div>
			)}

			{/* Add Notes Section */}
			<div
				style={{
					background: "#fff",
					borderRadius: 12,
					border: "1px solid #f0f0f0",
					marginBottom: 16,
					overflow: "hidden",
				}}
			>
				{!showNotes ? (
					<div
						onClick={() => setShowNotes(true)}
						style={{
							padding: "14px 20px",
							cursor: "pointer",
							display: "flex",
							alignItems: "center",
							gap: 10,
						}}
					>
						<EditOutlined style={{ color: "#999" }} />
						<Text style={{ color: "#666" }}>
							{patientNote ? "Edit booking notes" : "Add booking notes"}
						</Text>
						{patientNote && (
							<Text
								type="secondary"
								style={{
									marginLeft: "auto",
									fontSize: 13,
									maxWidth: 150,
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
								}}
							>
								{patientNote}
							</Text>
						)}
					</div>
				) : (
					<div style={{ padding: "16px 20px" }}>
						<Text type="secondary" style={{ fontSize: 13, marginBottom: 8, display: "block" }}>
							Booking notes (optional)
						</Text>
						<Input.TextArea
							value={patientNote}
							onChange={onChange}
							rows={3}
							placeholder="Add any specific details or preferences for your visit..."
							maxLength={250}
							showCount
							autoFocus
							style={{ resize: "none", marginBottom: 12 }}
						/>
						<Button size="small" onClick={() => setShowNotes(false)}>
							Done
						</Button>
					</div>
				)}
			</div>

			{/* Terms and Conditions */}
			{hasTerms && (
				<div
					style={{
						background: "#fff",
						borderRadius: 12,
						border: "1px solid #f0f0f0",
						marginBottom: 24,
						overflow: "hidden",
					}}
				>
					<Collapse
						ghost
						expandIconPosition="end"
						defaultActiveKey={["1"]}
						style={{ background: "transparent" }}
					>
						<Panel
							header={
								<Text strong style={{ fontSize: 14 }}>
									Terms and Conditions
								</Text>
							}
							key="1"
							style={{ borderBottom: "none" }}
						>
							<div
								style={{
									padding: "0 0 16px 0",
									fontSize: 13,
									color: "#666",
									lineHeight: 1.6,
								}}
								// biome-ignore lint/security/noDangerouslySetInnerHtml: Terms from practice settings
								dangerouslySetInnerHTML={{
									__html: currentPractice.termsAndConditions,
								}}
							/>
						</Panel>
					</Collapse>

					<div
						style={{
							padding: "12px 20px",
							borderTop: "1px solid #f0f0f0",
							background: acceptTerms ? "#f6ffed" : "#fafafa",
							transition: "background 0.2s",
						}}
					>
						<Checkbox
							checked={acceptTerms}
							onChange={(e) => {
								setAcceptTerms(e.target.checked);
								if (e.target.checked) {
									trackEvent("terms_accepted");
								}
							}}
						>
							<Text style={{ fontSize: 14 }}>
								I have read and accept the terms and conditions
							</Text>
						</Checkbox>
					</div>
				</div>
			)}

			{/* Confirm Button */}
			<Row justify="center" style={{ marginTop: 8 }}>
				<Col xs={24} sm={20} md={16} lg={14}>
					{confirmButton}
				</Col>
			</Row>

			{/* Subtle message */}
			<Text
				type="secondary"
				style={{
					display: "block",
					textAlign: "center",
					marginTop: 12,
					fontSize: 12,
				}}
			>
				{hasTerms && !acceptTerms
					? "Please accept the terms and conditions to continue"
					: "Your appointment will be confirmed after you click the button above"}
			</Text>
		</ContentContainer>
	);
}

const mapStateToProps = (state) => ({
	reviewBookings: getReviewAppointments(state.booking),
	currentPractice: getCurrentLocation(state.locations),
	saveBookingState: getSaveBookingState(state.booking),
	availabilityRequests: getAvailabilityRequests(state.booking),
	currentPatient: getCurrentPatient(state.booking),
});

export default connect(mapStateToProps, {
	...actions,
})(Confirm);
